import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Amplify } from 'aws-amplify'
import {
  AuthError,
  confirmResetPassword,
  confirmSignIn,
  resetPassword,
  signIn,
  signOut,
  updatePassword,
  type ConfirmResetPasswordInput,
  type ResetPasswordOutput,
  type SignInOutput,
  type UpdatePasswordInput,
} from 'aws-amplify/auth'
import { jwtDecode } from 'jwt-decode'
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs'

import { environment } from '../../environments/environment'
import amplifyConfig from '../amplify.config'
import { User } from '../models/user.model'

export interface UserCredentials {
  email: string
  password: string
}

export interface BaseCognitoToken {
  sub: string
  'cognito:groups': string[]
  iss: string
  origin_jti: string
  event_id: string
  token_use: string
  auth_time: number
  exp: number
  iat: number
  jti: string
}

export interface CognitoAccessToken extends BaseCognitoToken {
  client_id: string
  scope: string
  username: string
}

export interface CognitoIdToken extends BaseCognitoToken {
  'cognito:username': string
  aud: string
  email: string
  name: string
  family_name: string
  given_name: string
}

@Injectable({
  providedIn: 'root',
})
export class CognitoService {
  private userSubject!: ReplaySubject<User>

  public cognitoAccessTokenSubject =
    new BehaviorSubject<CognitoAccessToken | null>(null)
  public cognitoIdTokenSubject = new BehaviorSubject<CognitoIdToken | null>(
    null
  )

  public constructor(private readonly router: Router) {
    this.userSubject = new ReplaySubject<User>(1)
    Amplify.configure(amplifyConfig)
    this.loadTokensFromLocalStorage()
  }

  public get user(): Observable<User> {
    return this.userSubject
  }

  public async signIn(credentials: UserCredentials): Promise<void> {
    await signIn({
      username: credentials.email,
      password: credentials.password,
    })
      .then((output) => {
        this.handleSignInResult(output)
        return false
      })
      .catch((err: Error) => {
        return Promise.reject(err)
      })
  }

  public signOut(): Promise<void> {
    return signOut().then(() => {
      this.unsetUser()
      this.router.navigate(['/login'])
    })
  }

  private async handleSignInResult(result: SignInOutput): Promise<void> {
    switch (result.nextStep.signInStep) {
      case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED': {
        this.router.navigate(['/password-change'])
        break
      }
      case 'DONE': {
        this.loadTokensFromLocalStorage()
        this.router.navigate(['/'])
        break
      }
    }
  }

  public async confirmNewPassword(newPassword: string): Promise<void> {
    const result = await confirmSignIn({ challengeResponse: newPassword })

    return this.handleSignInResult(result)
  }

  public async handleResetPassword(
    username: string
  ): Promise<ResetPasswordOutput> {
    try {
      return await resetPassword({ username })
    } catch (error) {
      return Promise.reject(this.handleAmplifyError(error))
    }
  }

  public async handleConfirmResetPassword({
    username,
    confirmationCode,
    newPassword,
  }: ConfirmResetPasswordInput): Promise<void> {
    try {
      return await confirmResetPassword({
        username,
        confirmationCode,
        newPassword,
      })
    } catch (error) {
      return Promise.reject(this.handleAmplifyError(error))
    }
  }

  public async handleUpdatePassword({
    oldPassword,
    newPassword,
  }: UpdatePasswordInput): Promise<void> {
    try {
      return await updatePassword({ oldPassword, newPassword })
    } catch (error) {
      return Promise.reject(this.handleAmplifyError(error))
    }
  }

  private loadTokensFromLocalStorage(): void {
    const clientId = environment.COGNITO_USER_POOL_WEBCLIENT_ID
    const lastAuthUserKey = `CognitoIdentityServiceProvider.${clientId}.LastAuthUser`
    const username = localStorage.getItem(lastAuthUserKey)

    if (!username) return

    const idToken = localStorage.getItem(
      `CognitoIdentityServiceProvider.${clientId}.${username}.idToken`
    )
    const accessToken = localStorage.getItem(
      `CognitoIdentityServiceProvider.${clientId}.${username}.accessToken`
    )
    const refreshToken = localStorage.getItem(
      `CognitoIdentityServiceProvider.${clientId}.${username}.refreshToken`
    )

    if (!idToken || !accessToken || !refreshToken) {
      console.error('Tokens are missing')
      return
    }

    try {
      const decodedIdToken = jwtDecode<CognitoIdToken>(idToken)
      const decodedAccessToken = jwtDecode<CognitoAccessToken>(accessToken)

      this.cognitoAccessTokenSubject.next(decodedAccessToken)
      this.cognitoIdTokenSubject.next(decodedIdToken)

      if (this.cognitoIdTokenSubject.value)
        this.setUser(this.cognitoIdTokenSubject.value)
    } catch (e) {
      console.error('Failed to decode tokens', e)
    }
  }

  public getCurrentIdToken(): CognitoIdToken | null {
    return this.cognitoIdTokenSubject.value
  }

  private handleAmplifyError(error: unknown): Error {
    if (error instanceof AuthError) {
      return error
    }

    if (error instanceof Error) {
      return new Error(`${error.name}: ${error.message}`)
    }

    return new Error('Unknown error occurred')
  }

  private setUser(idToken: CognitoIdToken): void {
    const groups = idToken['cognito:groups']
    this.userSubject.next({
      firstName: idToken.given_name,
      lastName: idToken.family_name,
      name: `${idToken.given_name} ${idToken.family_name}`,
      email: idToken.email,
      id: idToken.sub,

      hasAssetAccess: groups?.includes('asset') || groups?.includes('admin'),
      hasSnapshotAccess:
        groups?.includes('snapshot') || groups?.includes('admin'),
      hasAuditAccess: groups?.includes('audit') || groups?.includes('admin'),
      hasAdminAccess: groups?.includes('admin'),
    })
  }

  private unsetUser(): void {
    this.userSubject?.unsubscribe()
    this.userSubject = new ReplaySubject<User>(1)
  }
}

export type AuthException = 'NotAuthorizedException' | 'UserNotFoundException'
export type ResetPasswordException =
  | 'CodeMismatchException'
  | 'PasswordHistoryPolicyViolationException'
  | 'ExpiredCodeException'
  | 'LimitExceededException'
